/**
 * Calendar & Meetings Constants
 * Labels, translations, and configuration for scheduling
 */

import { MeetingStatus, MeetingType, MeetingFormat, ContactRole } from '@/types/calendar.types';

export const MEETING_STATUS_LABELS: Record<MeetingStatus, { en: string; fr: string }> = {
  pending: { en: 'Pending', fr: 'En attente' },
  confirmed: { en: 'Confirmed', fr: 'Confirmé' },
  cancelled: { en: 'Cancelled', fr: 'Annulé' },
  completed: { en: 'Completed', fr: 'Terminé' },
  rescheduled: { en: 'Rescheduled', fr: 'Reprogrammé' },
};

export const CONTACT_ROLE_LABELS: Record<ContactRole, { en: string; fr: string }> = {
  internal: { en: 'Internal', fr: 'Interne' },
  broker: { en: 'Broker', fr: 'Courtier' },
  buyer: { en: 'Buyer', fr: 'Acheteur' },
  seller: { en: 'Seller', fr: 'Vendeur' },
  lender: { en: 'Lender', fr: 'Prêteur' },
  consultant: { en: 'Consultant', fr: 'Consultant' },
  business_lawyer: { en: 'Business Lawyer', fr: 'Avocat en droit des affaires' },
  immigration_lawyer: { en: 'Immigration Lawyer', fr: 'Avocat en droit de l\'immigration' },
  cpa: { en: 'CPA', fr: 'CPA' },
  notary: { en: 'Notary', fr: 'Notaire' },
};

export const MEETING_TYPE_LABELS: Record<MeetingType, { en: string; fr: string }> = {
  buyer_seller: { en: 'Buyer - Seller Meeting', fr: 'Réunion Acheteur - Vendeur' },
  buyer_lender: { en: 'Buyer - Lender Meeting', fr: 'Réunion Acheteur - Prêteur' },
  buyer_expert: { en: 'Buyer - Expert Meeting', fr: 'Réunion Acheteur - Expert' },
  seller_expert: { en: 'Seller - Expert Meeting', fr: 'Réunion Vendeur - Expert' },
  internal: { en: 'Internal Meeting', fr: 'Réunion Interne' },
};

export const MEETING_FORMAT_LABELS: Record<MeetingFormat, { en: string; fr: string; icon: string }> = {
  video: { en: 'Video Call', fr: 'Visioconférence', icon: 'Video' },
  phone: { en: 'Phone Call', fr: 'Appel téléphonique', icon: 'Phone' },
  in_person: { en: 'In Person', fr: 'En personne', icon: 'MapPin' },
};

export const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1h 30min' },
  { value: 120, label: '2 hours' },
];

export const TIME_SLOTS = Array.from({ length: 52 }, (_, i) => {
  const hour = Math.floor(i / 4) + 7; // Start at 7 AM
  const minute = (i % 4) * 15;
  const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  return { value: time, label: time };
});

export const CALENDAR_TRANSLATIONS = {
  en: {
    title: 'Calendar',
    subtitle: 'Manage your appointments and schedule',
    newMeeting: 'New Meeting',
    editMeeting: 'Edit Meeting',
    scheduleMeeting: 'Schedule Meeting',
    upcomingMeetings: 'Upcoming Meetings',
    pastMeetings: 'Past Meetings',
    noMeetings: 'No meetings scheduled',
    meetingTitle: 'Meeting Title',
    description: 'Description',
    date: 'Date',
    time: 'Time',
    duration: 'Duration',
    type: 'Meeting Type',
    participants: 'Participants',
    status: 'Status',
    actions: 'Actions',
    confirm: 'Confirm',
    cancel: 'Cancel',
    reschedule: 'Reschedule',
    markComplete: 'Mark as Complete',
    delete: 'Delete',
    save: 'Save',
    selectDeal: 'Select Deal (Optional)',
    selectParticipant: 'Select Participant',
    meetingScheduled: 'Meeting scheduled successfully',
    meetingUpdated: 'Meeting updated successfully',
    meetingCancelled: 'Meeting cancelled',
    meetingConfirmed: 'Meeting confirmed',
    meetingCompleted: 'Meeting marked as complete',
    confirmCancel: 'Are you sure you want to cancel this meeting?',
    confirmDelete: 'Are you sure you want to delete this meeting?',
    today: 'Today',
    tomorrow: 'Tomorrow',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
  },
  fr: {
    title: 'Calendrier',
    subtitle: 'Gérez vos rendez-vous et votre emploi du temps',
    newMeeting: 'Nouveau Rendez-vous',
    editMeeting: 'Modifier le Rendez-vous',
    scheduleMeeting: 'Planifier un Rendez-vous',
    upcomingMeetings: 'Rendez-vous à venir',
    pastMeetings: 'Rendez-vous passés',
    noMeetings: 'Aucun rendez-vous planifié',
    meetingTitle: 'Titre du Rendez-vous',
    description: 'Description',
    date: 'Date',
    time: 'Heure',
    duration: 'Durée',
    type: 'Type de Rendez-vous',
    participants: 'Participants',
    status: 'Statut',
    actions: 'Actions',
    confirm: 'Confirmer',
    cancel: 'Annuler',
    reschedule: 'Reprogrammer',
    markComplete: 'Marquer comme terminé',
    delete: 'Supprimer',
    save: 'Enregistrer',
    selectDeal: 'Sélectionner un Deal (Optionnel)',
    selectParticipant: 'Sélectionner un Participant',
    meetingScheduled: 'Rendez-vous planifié avec succès',
    meetingUpdated: 'Rendez-vous mis à jour avec succès',
    meetingCancelled: 'Rendez-vous annulé',
    meetingConfirmed: 'Rendez-vous confirmé',
    meetingCompleted: 'Rendez-vous marqué comme terminé',
    confirmCancel: 'Êtes-vous sûr de vouloir annuler ce rendez-vous?',
    confirmDelete: 'Êtes-vous sûr de vouloir supprimer ce rendez-vous?',
    today: "Aujourd'hui",
    tomorrow: 'Demain',
    thisWeek: 'Cette semaine',
    thisMonth: 'Ce mois',
  },
};
